<section class="addOrder w-full max-w-6xl mx-auto px-3 md:px-6 py-6 space-y-6" dir="rtl">

  <!-- Page header -->
  <header class="flex flex-col gap-1">
    <p class="text-base text-ink-500">{{ dateVal | date: "EEEE, d בMMMM y" : '' : 'he-IL' }}</p>
    <h1 class="text-3xl font-bold text-primary-700">{{ header }}</h1>
  </header>

  <!-- Latest actions strip (kept; existing component renders it) -->
  <app-latest-actions></app-latest-actions>

  <!-- Main two-pane layout: user picker + form -->
  <div class="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">

    <!-- ── User picker ──────────────────────────────────────────── -->
    <aside class="card-2026 lg:sticky lg:top-4 self-start max-h-[calc(100vh-2rem)] flex flex-col">
      <h2 class="text-lg font-semibold text-ink-800 mb-2">בחירת מתפלל</h2>

      <!-- Search -->
      <div class="relative mb-3">
        <input
          type="search"
          placeholder="חפש לפי שם..."
          aria-label="חיפוש מתפלל"
          [value]="userSearchTerm"
          (input)="onSearchChange($any($event.target).value)"
          class="input-2026 !pl-10 !pr-3"
        />
        <span class="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" aria-hidden="true">
          <i class="material-symbols-outlined text-xl">search</i>
        </span>
        <button
          *ngIf="userSearchTerm"
          type="button"
          (click)="clearSearch()"
          class="absolute left-9 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
          aria-label="נקה חיפוש"
        >
          <i class="material-symbols-outlined text-lg">close</i>
        </button>
      </div>

      <!-- Result list -->
      <div class="flex-1 overflow-y-auto rounded-lg border border-ink-100 divide-y divide-ink-100">
        <ng-container *ngIf="filteredUsers$ | async as filteredUsers">
          <button
            *ngFor="let user of filteredUsers"
            type="button"
            (click)="selectUser(user._id)"
            class="w-full text-right px-3 py-3 text-base text-ink-800 hover:bg-primary-50 focus:bg-primary-100 focus:outline-none transition-colors min-h-tap flex justify-between items-center gap-2"
            [class.bg-primary-100]="addOrder.get('userId')?.value === user._id"
            [class.font-semibold]="addOrder.get('userId')?.value === user._id"
            [attr.aria-pressed]="addOrder.get('userId')?.value === user._id"
          >
            <span>{{ user.firstName }} {{ user.lastName }}</span>
            <span *ngIf="addOrder.get('userId')?.value === user._id"
                  class="material-symbols-outlined text-primary-600"
                  aria-hidden="true">check_circle</span>
          </button>
          <div *ngIf="filteredUsers.length === 0" class="p-4 text-center text-sm text-ink-500">
            אין תוצאות עבור "{{ userSearchTerm }}"
          </div>
        </ng-container>
      </div>
    </aside>

    <!-- ── Form ─────────────────────────────────────────────────── -->
    <form
      novalidate
      [formGroup]="addOrder"
      (ngSubmit)="onSubmit()"
      class="card-2026 space-y-5"
      autocomplete="off"
    >
      <h2 class="text-xl font-semibold text-ink-800 pb-2 border-b border-ink-100">
        פרטי ההזמנה
      </h2>

      <!-- User select (mat-select; redundant with the aside for keyboard users) -->
      <div class="form-row-2026">
        <label class="form-row-2026__label">מתפלל</label>
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>בחר מתפלל</mat-label>
          <mat-select formControlName="userId">
            <mat-option *ngFor="let user of users$ | async" [value]="user._id">
              {{ user.firstName }} {{ user.lastName }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <!-- Order items (multi-select grid: every option visible, grouped by type) -->
      <div class="form-row-2026">
        <label class="form-row-2026__label" id="addOrderItemsLabel">מה נרכש</label>
        <app-order-items-picker
          formControlName="name"
          labelledBy="addOrderItemsLabel"
          placeholder="פרטי הזמנה (אפשר כמה) — לחץ או התחל להקליד"
        ></app-order-items-picker>
      </div>

      <!-- Parasha / Holiday autocomplete -->
      <div class="form-row-2026">
        <label class="form-row-2026__label" for="addOrderParasha">פרשה או חג</label>
        <mat-form-field appearance="outline" class="w-full">
          <input
            id="addOrderParasha"
            type="text"
            matInput
            placeholder="חפש פרשה או חג..."
            [formControl]="parashaFilterCtrl"
            [matAutocomplete]="auto"
          />
          <mat-autocomplete
            #auto="matAutocomplete"
            [displayWith]="displayFn"
            class="parasha-autocomplete-panel"
          >
            <mat-optgroup label="פרשות השבוע">
              <mat-option
                *ngFor="let p of filteredParashotOptions | async"
                [value]="p.heb"
                (onSelectionChange)="selectOption(p)"
              >
                {{ p.heb }}
              </mat-option>
            </mat-optgroup>
            <mat-optgroup label="חגים ומועדים">
              <mat-option
                *ngFor="let h of filteredHolidaysOptions | async"
                [value]="h.heb"
                (onSelectionChange)="selectOption(h)"
              >
                {{ h.heb }}
              </mat-option>
            </mat-optgroup>
          </mat-autocomplete>
        </mat-form-field>
      </div>

      <!-- Money fields side by side on desktop -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="form-row-2026" *ngIf="!isCreditOnly">
          <app-money-input
            formControlName="price"
            label="עלות (₪)"
          ></app-money-input>
        </div>
        <div class="form-row-2026">
          <app-money-input
            formControlName="pricePaid"
            [label]="(isCreditOnly ? 'סכום' : 'שולם') + ' (₪)'"
          ></app-money-input>
        </div>
      </div>

      <!-- Invoice & note -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="form-row-2026">
          <label class="form-row-2026__label" for="addOrderInvoice">מס' קבלה</label>
          <input
            id="addOrderInvoice"
            type="text"
            placeholder="ABC-1001"
            class="input-2026"
            formControlName="orderInvoice"
            name="orderInvoice"
          />
        </div>
        <div class="form-row-2026">
          <label class="form-row-2026__label" for="addOrderNote">הערות</label>
          <input
            id="addOrderNote"
            type="text"
            placeholder="טקסט חופשי"
            class="input-2026"
            formControlName="note"
            name="note"
          />
        </div>
      </div>

      <!-- Submit -->
      <div class="pt-2 flex flex-col-reverse md:flex-row md:justify-end md:items-center gap-3">
        <button
          type="submit"
          class="btn-2026--primary w-full md:w-auto md:min-w-[220px]"
          [disabled]="loading || addOrder.invalid"
        >
          <span *ngIf="!loading" class="flex items-center gap-2">
            <i class="material-symbols-outlined" aria-hidden="true">save</i>
            הזן הזמנה
          </span>
          <span *ngIf="loading" class="flex items-center gap-2">
            <mat-spinner diameter="22"></mat-spinner>
            <span>שומר...</span>
          </span>
        </button>
      </div>
    </form>
  </div>
</section>

<!-- ── Order confirmation dialog ──────────────────────────────────── -->
<div
  *ngIf="confirmingOrder"
  class="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
  role="dialog"
  aria-modal="true"
  aria-labelledby="orderConfirmTitle"
  (click)="cancelOrderConfirm()"
  dir="rtl"
>
  <div
    class="bg-surface rounded-2xl shadow-elevated max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden"
    (click)="$event.stopPropagation()"
  >
    <!-- Header -->
    <div class="p-4 bg-primary-500 text-white flex justify-between items-center gap-3">
      <h2 id="orderConfirmTitle" class="text-lg font-bold flex items-center gap-2">
        <i class="material-symbols-outlined" aria-hidden="true">fact_check</i>
        אישור פרטי ההזמנה
      </h2>
      <button
        type="button"
        (click)="cancelOrderConfirm()"
        class="p-1 hover:bg-white/20 rounded-lg transition-colors"
        aria-label="סגור"
      >
        <i class="material-symbols-outlined" aria-hidden="true">close</i>
      </button>
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-y-auto p-4 space-y-4">
      <p class="text-sm text-ink-600">
        בדוק שהפרטים נכונים לפני שמירת ההזמנה. לאחר אישור הפעולה תיכנס להיסטוריה ותעדכן את יתרת המתפלל.
      </p>

      <!-- User -->
      <div class="rounded-xl bg-bg p-3 border border-ink-100">
        <div class="text-sm text-ink-500 font-medium">מתפלל</div>
        <div class="text-xl font-bold text-ink-800 mt-1">
          {{ confirmingOrder.user?.firstName }} {{ confirmingOrder.user?.lastName }}
        </div>
        <div class="text-sm text-ink-500 mt-1">
          יתרה נוכחית:
          <span class="money font-semibold"
                [class.money--credit]="(confirmingOrder.user?.balance ?? 0) > 0"
                [class.money--debt]="(confirmingOrder.user?.balance ?? 0) < 0"
                [class.money--zero]="confirmingOrder.user?.balance === 0">
            {{ confirmingOrder.user?.balance | currency : 'ILS' : 'symbol' : '1.0-0' }}
          </span>
        </div>
      </div>

      <!-- Parasha + items -->
      <dl class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <dt class="text-sm font-medium text-ink-500">פרשה / חג</dt>
          <dd class="text-lg font-semibold text-ink-800 mt-0.5">{{ confirmingOrder.parasha }}</dd>
        </div>
        <div *ngIf="confirmingOrder.orderInvoice">
          <dt class="text-sm font-medium text-ink-500">מס' קבלה</dt>
          <dd class="text-lg font-semibold text-ink-800 mt-0.5">{{ confirmingOrder.orderInvoice }}</dd>
        </div>
      </dl>

      <div *ngIf="confirmingOrder.items.length > 0">
        <dt class="text-sm font-medium text-ink-500">פריטים</dt>
        <dd class="flex flex-wrap gap-1.5 mt-1">
          <span *ngFor="let item of confirmingOrder.items"
                class="inline-block px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-sm font-medium">
            {{ item }}
          </span>
        </dd>
      </div>

      <!-- Money summary — 3-col on every width; numbers are short. -->
      <div class="rounded-xl border border-ink-100 overflow-hidden">
        <div class="grid grid-cols-3 divide-x divide-x-reverse divide-ink-100 text-center">
          <div class="p-2 sm:p-3">
            <div class="text-xs text-ink-500 font-medium">עלות</div>
            <div class="money text-base sm:text-lg font-bold mt-1">
              {{ confirmingOrder.price | currency : 'ILS' : 'symbol' : '1.0-0' }}
            </div>
          </div>
          <div class="p-2 sm:p-3">
            <div class="text-xs text-ink-500 font-medium">שולם</div>
            <div class="money text-base sm:text-lg font-bold mt-1">
              {{ confirmingOrder.pricePaid | currency : 'ILS' : 'symbol' : '1.0-0' }}
            </div>
          </div>
          <div class="p-2 sm:p-3">
            <div class="text-xs text-ink-500 font-medium">נותר</div>
            <div class="money text-base sm:text-lg font-bold mt-1"
                 [class.money--debt]="confirmingOrder.price > confirmingOrder.pricePaid"
                 [class.text-success]="confirmingOrder.pricePaid >= confirmingOrder.price">
              <ng-container *ngIf="confirmingOrder.pricePaid >= confirmingOrder.price; else hasLeft">
                שולם
              </ng-container>
              <ng-template #hasLeft>
                {{ confirmingOrder.price - confirmingOrder.pricePaid | currency : 'ILS' : 'symbol' : '1.0-0' }}
              </ng-template>
            </div>
          </div>
        </div>
      </div>

      <!-- Net balance impact -->
      <div class="rounded-xl p-3 flex items-center justify-between"
           [class.bg-success-soft]="confirmingOrder.pricePaid > confirmingOrder.price"
           [class.bg-danger-soft]="confirmingOrder.pricePaid < confirmingOrder.price"
           [class.bg-bg]="confirmingOrder.pricePaid === confirmingOrder.price">
        <span class="text-base font-medium text-ink-700">השפעה על יתרת המתפלל</span>
        <span class="money text-lg font-bold"
              [class.money--credit]="confirmingOrder.pricePaid > confirmingOrder.price"
              [class.money--debt]="confirmingOrder.pricePaid < confirmingOrder.price"
              [class.money--zero]="confirmingOrder.pricePaid === confirmingOrder.price">
          <ng-container *ngIf="confirmingOrder.pricePaid > confirmingOrder.price">
            +{{ confirmingOrder.pricePaid - confirmingOrder.price | currency : 'ILS' : 'symbol' : '1.0-0' }}
          </ng-container>
          <ng-container *ngIf="confirmingOrder.pricePaid < confirmingOrder.price">
            −{{ confirmingOrder.price - confirmingOrder.pricePaid | currency : 'ILS' : 'symbol' : '1.0-0' }}
          </ng-container>
          <ng-container *ngIf="confirmingOrder.pricePaid === confirmingOrder.price">
            ללא שינוי
          </ng-container>
        </span>
      </div>

      <!-- Note -->
      <div *ngIf="confirmingOrder.note">
        <dt class="text-sm font-medium text-ink-500">הערות</dt>
        <dd class="text-base text-ink-700 mt-0.5 italic">{{ confirmingOrder.note }}</dd>
      </div>

      <!-- Admin attribution -->
      <div class="text-sm text-ink-500 pt-2 border-t border-ink-100">
        ההזמנה תירשם בהיסטוריה ע"י
        <span class="font-semibold text-ink-700">{{ confirmingOrder.adminName || 'אדמין' }}</span>.
      </div>
    </div>

    <!-- Footer -->
    <div class="p-4 border-t border-ink-100 bg-bg flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
      <button
        type="button"
        (click)="cancelOrderConfirm()"
        class="btn-2026--ghost w-full sm:flex-1"
        [disabled]="loading"
      >
        ביטול / חזרה לעריכה
      </button>
      <button
        type="button"
        (click)="confirmOrderSubmit()"
        class="btn-2026--primary w-full sm:flex-1"
        [disabled]="loading"
      >
        <span *ngIf="!loading" class="flex items-center gap-2 justify-center">
          <i class="material-symbols-outlined" aria-hidden="true">save</i>
          אישור ושמירה
        </span>
        <span *ngIf="loading" class="flex items-center gap-2 justify-center">
          <mat-spinner diameter="22"></mat-spinner>
          <span>שומר...</span>
        </span>
      </button>
    </div>
  </div>
</div>

<style>
  .parasha-autocomplete-panel .mat-autocomplete-panel {
    max-height: 250px !important;
    overflow-y: auto;
  }
</style>
